import { fetchResource } from "@src/app/utils";
import { LOAD_LIMIT, auth, fetchFirestoreQuery, firestore } from "@src/app/utils/firebase";
import {
	type DocumentData,
	type QueryDocumentSnapshot,
	collection,
	documentId,
	limit,
	orderBy,
	query,
	startAfter,
	where,
} from "firebase/firestore";
import { z } from "zod";
import { chatUnreadMessagesSchema, conversationSchema, messageSchema, searchUserSchema } from "./schemas";

/**
 * Get number of unread chat messages
 */
export const getUnreadCount = () => fetchResource("chat/messages/unread", chatUnreadMessagesSchema);

export const getMessagesQuery = (uuid: string, lastItem?: QueryDocumentSnapshot<DocumentData>) => {
	return {
		query: query(
			collection(firestore, `conversations/${uuid}/messages`),
			orderBy("created_at", "desc"),
			limit(LOAD_LIMIT),
			...(lastItem ? [startAfter(lastItem)] : []),
		),
		validator: z.array(messageSchema),
	};
};
/**
 * Get messages
 */
export const getMessages = async (uuid: string, lastItem?: QueryDocumentSnapshot<DocumentData>) =>
	fetchFirestoreQuery(getMessagesQuery(uuid, lastItem));

export const getConversationsQuery = (lastItem?: QueryDocumentSnapshot<DocumentData>) => {
	return {
		query: query(
			collection(firestore, "conversations"),
			where("participants", "array-contains", auth.currentUser?.uid),
			orderBy("last_activity_at", "desc"),
			limit(LOAD_LIMIT),
			...(lastItem ? [startAfter(lastItem)] : []),
		),
		validator: z.array(conversationSchema),
	};
};
/**
 * Get list of conversations
 */
export const getConversations = async (lastItem?: QueryDocumentSnapshot<DocumentData>) =>
	fetchFirestoreQuery(getConversationsQuery(lastItem));

export const getConversationQuery = (uuid: string) => {
	return {
		query: query(
			collection(firestore, "conversations"),
			where("participants", "array-contains", auth.currentUser?.uid),
			where(documentId(), "==", uuid),
		),
		validator: conversationSchema,
	};
};
/**
 * Get specific conversation details by conversation UUID
 */
export const getConversation = (uuid: string) => fetchFirestoreQuery(getConversationQuery(uuid), true);

/**
 * Send text message
 */
export const sendMessageText = (text: string, conversation_uuid?: string, user_id?: string) =>
	fetchResource("chat/messages", messageSchema, "POST", { conversation_uuid, user_id, kind: "text", text });

/**
 * Send audio message
 */
export const sendAudioMessage = (formData: FormData) =>
	fetchResource("chat/messages", messageSchema, "POST", formData, { formData: true });

/**
 * Mark conversation messages as read
 */
export const postReadMessage = (uuid: string) =>
	fetchResource(`chat/conversations/${uuid}/read`, z.object({}), "POST", undefined, { emptyResponse: true });

export const searchMutualFollowers = (term: string) =>
	fetchResource("chat/contacts/search", z.array(searchUserSchema), "POST", { term });

export const getConversationUuid = (userUuid: string) =>
	fetchResource(`chat/conversations/find/direct/${userUuid}`, z.object({ uuid: z.string() }));
